import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import EditableCell from '../EditableCell';
import WigTransferModal from '../WigTransferModal';
import { useWigTransfer } from '../../contexts/WigTransferContext';
import ClientRejectionModal from './ClientRejectionModal';
import AutoCompleteButton from '../buttons/AutoCompleteButton';
import AttachMediaModal from './AttachMediaModal';
import { 
  getCurrentTimestamp, 
  utcToLocalDate, localDateTimeToUTC, formatDateTimeForUser
} from '../../utils/dateUtils';
import { API_URL } from '../../config/api';
import { getRoomsData, getRoomTasks, getRoomWorkers } from '../../services/roomsCache';
import ReminderFormModal from '../Reminders/ReminderFormModal';
import { remindersService } from '../../services/remindersService';
import '../styles/ProductionSchedule.css';
import { getCurrentUser } from '../../utils/auth';
const ProductionSchedule = ({
  orderNo,
  onAutoCompleteSuccess: onAutoCompleteSuccessFromParent,
  onDataChange,
  customerfullname
}) => {
  const [showRejectedTasks, setShowRejectedTasks] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { openTransferModal } = useWigTransfer();
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(new Set());
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [activeOrderReminders, setActiveOrderReminders] = useState([]);

  const [setRoomTasks] = useState({});

  const currentUser = getCurrentUser();

  const fetchOrderReminders = useCallback(async () => {
    if (!currentUser || !orderNo) return;
    try {
      const all = await remindersService.getAll(currentUser);
      setActiveOrderReminders(
        all.filter(r => String(r.LinkedOrderNo) === String(orderNo) && !r.IsCompleted)
      );
    } catch (_) {}
  }, [currentUser, orderNo]);

  useEffect(() => { fetchOrderReminders(); }, [fetchOrderReminders]);

  // Complete order data state - same as WorkOrderProfile
  const [orderData, setOrderData] = useState({
    orderDetails: null,
    customerInfo: null,
    wigDetails: null,
    roomTasks: [],
    loaded: false
  });
    const { roomTasks, orderDetails } = orderData;

  const [roomsData, setRoomsData] = useState({
    Pomona: { rooms: [], tasks: {}, workers: {} },
    Lakewood: { rooms: [], tasks: {}, workers: {} },
    Israel: { rooms: [], tasks: {}, workers: {} },
    China: { rooms: [], tasks: {}, workers: {} }
  });

  const [newTask, setNewTask] = useState({
    location: '',
    roomNo: '',
    roomTask: '',
    assignedWorker: '',
    taskDueDate: '',
    taskNotes: '',
    qcNotes: '',
    status: 'Not Started'
  });

  // Status options for the select dropdown
  const statusOptions = [
    { value: '', label: '' },
    { value: 'Pending', label: 'Pending' },
    { value: 'In-Process', label: 'In-Process' },
    { value: 'Task Completed', label: 'Task Completed' },
    { value: 'On Hold', label: 'On Hold' },
    { value: 'Not Started', label: 'Not Started' },
    { value: 'Rejected', label: 'Rejected' }
  ];

  // Fetch complete order data - same as WorkOrderProfile
  const fetchOrderData = useCallback(async (orderNumber) => {
    if (!orderNumber || orderNumber === 'New Order') {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Always do full fetch to ensure all data is loaded correctly
      const response = await axios.get(`${API_URL}/api/orders/order/${orderNumber}`);

      if (response.data) {
        const data = response.data;

        // Format the data to match expected structure
        setOrderData({
          orderDetails: {
            ...data,
            orderNo: data.orderNo,
            orderDate: data.orderDate,
            dueDate: data.dueDate,
            orderStatus: data.orderStatus,
            orderType: data.orderType,
            customerName: data.customerName,
            customerPhone: data.customerPhone,
            customerEmail: data.customerEmail,
            customerAddress: data.customerAddress,
            customerCity: data.customerCity,
            customerState: data.customerState,
            customerZip: data.customerZip,
            wigBarCode: data.wigBarCode,
            wigSerialNo: data.wigSerialNo,
            orderNotes: data.orderNotes
          },
          customerInfo: {
            customerName: data.customerName,
            ID: data.customerNo,
            phone: data.customerPhone,
            email: data.customerEmail,
            address: data.customerAddress,
            city: data.customerCity,
            state: data.customerState,
            zip: data.customerZip,
            orderType: data.orderType
          },
          wigDetails: {
            wigType: data.wigType,
            netSize: data.netSize,
            netType: data.netType,
            baseColor: data.baseColor,
            hairLength: data.wigLength,
            hairType: data.hairType,
            laceType: data.laceType,
            cutStyle: data.cutStyle,
            notes: data.orderNotes
          },
          roomTasks: data.roomTasks || [],
          loaded: true
        });

        console.log('✅ Order data loaded successfully');
      }
    } catch (err) {
      console.error('❌ Error fetching order data:', err);
      setError(`Failed to load order data. Error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch rooms data from global cache (shared across all ProductionSchedule instances)
  useEffect(() => {
    const loadRoomsFromCache = async () => {
      try {
        const cachedRooms = await getRoomsData();
        setRoomsData(cachedRooms);
      } catch (error) {
        console.error('Failed to load rooms from cache:', error);
      }
    };

    loadRoomsFromCache();
  }, []);


  // Listen for order auto-completion event
  useEffect(() => {
    const handleOrderAutoCompleted = (event) => {
      const { orderNo: completedOrderNo } = event.detail;
      if (completedOrderNo === orderNo) {
        console.log(`🔁 Refreshing ProductionSchedule for order ${orderNo}`);
        fetchOrderData(orderNo);
      }
    };

    window.addEventListener('wigTransferCompleted', handleOrderAutoCompleted);

    return () => {
      window.removeEventListener('wigTransferCompleted', handleOrderAutoCompleted);
    };
  }, [fetchOrderData, orderNo]);



  // Load order data when orderNo changes
  useEffect(() => {
    if (orderNo) {
      fetchOrderData(orderNo);
    }
  }, [orderNo, fetchOrderData]);
      useEffect(() => {
    const handleAutoCompleteEvent = (event) => {
        const { orderNo, completedBy, tasksCompleted } = event.detail;
        
        console.log('🔄 Auto-complete event received:', orderNo);
        
        // Refresh the specific order's room tasks
        if (roomTasks[orderNo]) {
        fetchRoomTasks(orderNo);
        }
        
    };
    
    window.addEventListener('orderAutoCompleted', handleAutoCompleteEvent);
    
    return () => {
        window.removeEventListener('orderAutoCompleted', handleAutoCompleteEvent);
    };
    }, [roomTasks]);
  // Refresh function to pass to EditableCell
  const handleRefreshSchedule = useCallback(() => {
    console.log('🔄 Refreshing production schedule after save...');
    if (orderData.orderDetails?.orderNo) {
      fetchOrderData(orderData.orderDetails.orderNo);
    }
  }, [fetchOrderData, orderData.orderDetails?.orderNo]);
  // Load room data in background - uses global cache
  const loadRoomDataInBackground = async (location, roomNo) => {
    if (!location || !roomNo) {
      return;
    }

    try {
      // Fetch tasks and workers in parallel from cache
      const [tasks, workers] = await Promise.all([
        getRoomTasks(location, roomNo),
        getRoomWorkers(location, roomNo)
      ]);

      // Update local state with cached data
      setRoomsData(prev => ({
        ...prev,
        [location]: {
          ...prev[location],
          tasks: {
            ...(prev[location]?.tasks || {}),
            [roomNo]: tasks
          },
          workers: {
            ...(prev[location]?.workers || {}),
            [roomNo]: workers
          }
        }
      }));
    } catch (error) {
      console.error('Error loading room data:', error);
    }
  };

  const updateTaskField = async (taskId, field, value) => {
    try {
        console.log(`🔄 Updating task ${taskId}:`, { field, value });

        const response = await fetch(`${API_URL}/api/update-room-task-field/${taskId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            [field]: value
        })
        });

        const result = await response.json();

        if (result.success) {
        // ✅ Update local state AFTER API success
        // Map field name to both camelCase and PascalCase versions
        const fieldLower = field.charAt(0).toLowerCase() + field.slice(1);
        const fieldUpper = field.charAt(0).toUpperCase() + field.slice(1);

        setOrderData(prevData => ({
            ...prevData,
            roomTasks: prevData.roomTasks.map(task => {
            if ((task.ID || task.id) === parseInt(taskId)) {
                console.log(`🔄 Updating local state for task ${taskId}:`, {
                field,
                oldValue: task[field] || task[fieldLower] || task[fieldUpper],
                newValue: value
                });
                // Update both camelCase and PascalCase versions to handle inconsistencies
                return {
                  ...task,
                  [field]: value,
                  [fieldLower]: value,
                  [fieldUpper]: value
                };
            }
            return task;
            })
        }));

        // Notify parent of data change for fields that affect subrow display
        const fieldsAffectingSubrow = ['AssignedWorker', 'RoomNo', 'TaskStatus', 'ScanInDate', 'ScanOutDate'];
        if (onDataChange && fieldsAffectingSubrow.includes(field)) {
          onDataChange(orderNo);
        }

        } else {
        throw new Error(result.error || 'Update failed');
        }
    } catch (error) {
        console.error('❌ Failed to update task:', error);
        alert(`Failed to update ${field}: ${error.message}`);
        throw error;
    }
  };
  

  // Add task function - same as WorkOrderProfile
  const addTaskToOrder = async () => {
    if (!orderData.orderDetails || !orderData.orderDetails.orderNo) {
      alert('No order number found. Cannot add task.');
      return;
    }
    
    try {
      const taskData = {
        orderNo: parseInt(orderData.orderDetails.orderNo),
        roomNo: parseInt(newTask.roomNo) || 0,
        wigBarCode: orderData.orderDetails.wigBarCode || '',
        wigSerialNo: orderData.orderDetails.wigSerialNo || '',
        roomTask: newTask.roomTask || '',
        taskNotes: newTask.taskNotes || '',
        assignedWorker: newTask.assignedWorker || '',
        entryDate: getCurrentTimestamp(),
        transferFromRoomNo: null,
        transferToRoomNo: null,
        parentRoomNo: null,
        taskDueDate: newTask.taskDueDate || null,
        taskStatus: newTask.status || 'Not Started',
        sortOrder: 0
      };


      const response = await axios.post(`${API_URL}/api/add-task`, taskData);
      
      const newTaskWithId = {
        id: response.data.id,
        ID: response.data.id,
        roomNo: newTask.roomNo,
        roomTask: newTask.roomTask,
        assignedWorker: newTask.assignedWorker,
        taskDueDate: newTask.taskDueDate,
        location: newTask.location,
        status: newTask.status || 'Pending',
        taskStatus: newTask.status || 'Pending',
        taskNotes: newTask.taskNotes,
        qcNotes: newTask.qcNotes
      };
      
      // Update local state with the new task
      setOrderData(prev => ({
        ...prev,
        roomTasks: [...(prev.roomTasks || []), newTaskWithId]
      }));
      
      // Close the modal and reset the form
      setShowAddTaskModal(false);
      setNewTask({
        location: '',
        roomNo: '',
        roomTask: '',
        assignedWorker: '',
        taskDueDate: '',
        taskNotes: '',
        qcNotes: '',
        status: 'Pending'
      });
      
    } catch (error) {
      console.error('Error adding task:', error);
      alert(`Failed to add task. Error: ${error.message}`);
    }
  };

  // Delete task function - same as WorkOrderProfile
  const deleteTask = async (taskId) => {
    const actualTaskId = taskId?.ID || taskId?.id || taskId;
    
    if (!actualTaskId) {
      console.error('No task ID provided:', taskId);
      alert('No task ID provided for deletion');
      return;
    }
    
    try {
      console.log('Deleting task with ID:', actualTaskId);
      
      const response = await axios.delete(`${API_URL}/api/delete-room-task/${actualTaskId}`);
      
      if (response.data.success) {
        setOrderData(prev => ({
          ...prev,
          roomTasks: prev.roomTasks.filter(task => {
            const currentTaskId = task.ID || task.id;
            return currentTaskId !== actualTaskId;
          })
        }));
      } else {
        throw new Error('Backend reported failure');
      }
      
    } catch (error) {
      console.error('Error deleting task:', error);
      alert(`Failed to delete task. Error: ${error.response?.data?.error || error.message}`);
    }
  };

  // Room change handler
  const handleRoomChange = async (location, roomNo) => {
    const currentTask = newTask.roomTask;
    const currentWorker = newTask.assignedWorker;
    const isEditing = !!editingTask;
    
    setNewTask(prev => ({
      ...prev,
      location,
      roomNo,
      roomTask: isEditing ? prev.roomTask : '',
      assignedWorker: isEditing ? prev.assignedWorker : ''
    }));
    
    if (location && roomNo) {
      await loadRoomDataInBackground(location, roomNo);
      
      setTimeout(() => {
        if (isEditing && (currentTask || currentWorker)) {
          setNewTask(prev => ({
            ...prev,
            roomTask: currentTask,
            assignedWorker: currentWorker
          }));
        }
      }, 100);
    }
  };

  // Start editing task
  const startEditingTask = (task) => {
    setEditingTask(task);
    setShowAddTaskModal(true);
    
     let formattedDate = utcToLocalDate(getCurrentTimestamp());
      if (task.taskDueDate || task.TaskDueDate) {
        const dateValue = task.taskDueDate || task.TaskDueDate;
        if (dateValue) {
          formattedDate = utcToLocalDate(dateValue);
        }
      }
    
    setNewTask({
      location: task.location || task.Location || '',
      roomNo: (task.roomNo || task.RoomNo || '').toString(),
      roomTask: task.roomTask || task.RoomTask || '',
      assignedWorker: task.assignedWorker || task.AssignedWorker || '',
      taskDueDate: formattedDate,
      taskNotes: task.taskNotes || task.TaskNotes || '',
      qcNotes: task.qcNotes || task.QC_Notes || '',
      status: task.status || task.taskStatus || task.TaskStatus || 'Pending'
    });
    
    const location = task.location || task.Location || '';
    const roomNo = task.roomNo || task.RoomNo || '';
    
    if (location && roomNo) {
      loadRoomDataInBackground(location, roomNo);
    }
  };

  // Update task function - same as WorkOrderProfile
  const updateTask = async () => {
    const taskId = editingTask?.ID || editingTask?.id;

    if (!editingTask || !taskId) {
      console.error('No task or task ID found:', { editingTask, taskId });
      alert('No task selected for editing or missing task ID');
      return;
    }
    
    try {
      if (!newTask.roomTask.trim()) {
        alert('Room task is required');
        return;
      }
      
      const taskData = {
        roomNo: newTask.roomNo ? parseInt(newTask.roomNo) : 0,
        roomTask: newTask.roomTask.trim(),
        assignedWorker: newTask.assignedWorker || '',
        taskNotes: newTask.taskNotes || '',
        qcNotes: newTask.qcNotes || '',
        taskDueDate: newTask.taskDueDate || null,
        taskStatus: newTask.status || 'Pending'
      };

      console.log('Updating task with ID:', taskId, 'with data:', taskData);
      
      const response = await axios.put(`${API_URL}/api/update-room-task/${taskId}`, taskData);
      
      console.log('Task updated successfully:', response.data);
      
      if (response.data.success) {
        setOrderData(prev => ({
          ...prev,
          roomTasks: prev.roomTasks.map(task => {
            const currentTaskId = task.ID || task.id;
            return currentTaskId == taskId
              ? {
                  ...task,
                  location: newTask.location,
                  roomNo: newTask.roomNo ? parseInt(newTask.roomNo) : task.roomNo,
                  roomTask: newTask.roomTask,
                  assignedWorker: newTask.assignedWorker,
                  taskDueDate: newTask.taskDueDate,
                  taskNotes: newTask.taskNotes,
                  qcNotes: newTask.qcNotes,
                  status: newTask.status,
                  taskStatus: newTask.status
                }
              : task;
          })
        }));

        setShowAddTaskModal(false);
        setEditingTask(null);
        setNewTask({
          location: '',
          roomNo: '',
          roomTask: '',
          assignedWorker: '',
          taskDueDate: '',
          taskNotes: '',
          qcNotes: '',
          status: 'Pending'
        });
      } else {
        throw new Error('Backend reported failure');
      }

    } catch (error) {
      console.error('Error updating task:', error);
      
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.details || 
                          error.message || 
                          'Unknown error occurred';
      
      alert(`Failed to update task. Error: ${errorMessage}`);
    }
  };

  // Rooms that should always sort last (customer pickup rooms)
  const LAST_PRIORITY_ROOMS = new Set([7001, 21001, 32001]);
  const FIRST_PRIORITY_ROOMS = new Set([9001, 20001, 31001]);
  // Sorting function - same as WorkOrderProfile
  const sortTasksAdvanced = (tasks) => {
    return [...tasks].sort((a, b) => {
      // Step 0: Rooms 7001, 21001, 32001 always go last
      const aRoomNo = parseInt(a.roomNo || a.RoomNo) || 0;
      const bRoomNo = parseInt(b.roomNo || b.RoomNo) || 0;
      const aIsLastPriority = LAST_PRIORITY_ROOMS.has(aRoomNo);
      const bIsLastPriority = LAST_PRIORITY_ROOMS.has(bRoomNo);

      if (aIsLastPriority && !bIsLastPriority) return 1;
      if (!aIsLastPriority && bIsLastPriority) return -1;

      //step 1: Rooms 9001, 20001, 31001 always go first
      

      const aIsFirstPriority = FIRST_PRIORITY_ROOMS.has(aRoomNo);
      const bIsFirstPriority = FIRST_PRIORITY_ROOMS.has(bRoomNo);

      if (aIsFirstPriority && !bIsFirstPriority) return -1;
      if (!aIsFirstPriority && bIsFirstPriority) return 1;



      // Sort by Status Priority
      const getStatusPriority = (status) => {
        if (!status) return 6;
        const statusLower = status.toLowerCase();
        
        if (statusLower.includes('completed') || statusLower.includes('task_completed')) return 1;
        if (statusLower.includes('in-process') || 
            statusLower.includes('in-progress') || 
            statusLower.includes('progress')) return 2;
        if (statusLower.includes('pending')) return 3;
        if (statusLower.includes('not started') || statusLower.includes('unknown')) return 4;
        if (statusLower.includes('hold')) return 5;
        if (statusLower.includes('reject')) return 7;
        
        return 5;
      };
      
      const aStatusPriority = getStatusPriority(a.status || a.taskStatus);
      const bStatusPriority = getStatusPriority(b.status || b.taskStatus);
      
      if (aStatusPriority !== bStatusPriority) {
        return aStatusPriority - bStatusPriority;
      }
      
      // Sort by Transfer Chain Order
      const getTransferChainPosition = (task, allTasks) => {
        const currentRoom = task.roomNo || task.RoomNo;
        
        const findChainStart = (roomNo, visited = new Set()) => {
          if (visited.has(roomNo)) return 999;
          visited.add(roomNo);
          
          const parentTransfer = allTasks.find(t => 
            (t.transferToRoomNo || t.TransferToRoomNo) === roomNo
          );
          if (!parentTransfer) {
            return 0;
          }
          return findChainStart(parentTransfer.roomNo || parentTransfer.RoomNo, visited) + 1;
        };
        
        return findChainStart(currentRoom);
      };
      
      const aChainPosition = getTransferChainPosition(a, tasks);
      const bChainPosition = getTransferChainPosition(b, tasks);
      
      if (aChainPosition !== bChainPosition) {
        return aChainPosition - bChainPosition;
      }
      
      // Sort by dates
      const getTaskDate = (task) => {
        if (task.scanOutDate || task.ScanOutDate) return new Date(task.scanOutDate || task.ScanOutDate);
        if (task.scanInDate || task.ScanInDate) return new Date(task.scanInDate || task.ScanInDate);
        if (task.taskDueDate || task.TaskDueDate) return new Date(task.taskDueDate || task.TaskDueDate);
        return null;
      };
      
      const aDate = getTaskDate(a);
      const bDate = getTaskDate(b);
      
      if (aDate && bDate) {
        return bDate - aDate;
      }
      
      if (aDate && !bDate) return -1;
      if (!aDate && bDate) return 1;
      
      // Final fallback to room number
      const aRoom = parseInt((a.roomNo || a.RoomNo)) || 999999;
      const bRoom = parseInt((b.roomNo || b.RoomNo)) || 999999;
      
      return aRoom - bRoom;
    });
  };

  
  const getStatusClass = (status) => {
    if (!status) return 'badge-secondary';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('completed')) return 'badge-success';
    if (statusLower.includes('in-process') || statusLower.includes('progress')) return 'badge-warning';
    if (statusLower.includes('pending')) return 'badge-info';
    if (statusLower.includes('reject')) return 'badge-danger';
    return 'badge-secondary';
  };


  // Show loading state
  if (isLoading) {
    return (
      <div className="production-schedule-container">
        <div className="card shadow-sm production-card-bg"> 
          <div className="card-body text-center py-4">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-2">Loading production schedule...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="production-schedule-container">
        <div className="card shadow-sm">
          <div className="card-body text-center py-4">
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Apply sorting and filtering
  const allSortedTasks = sortTasksAdvanced(roomTasks || []);

// Helper function to check if a task is rejected
const isRejectedTask = (task) => {
  const status = task.status || task.taskStatus || '';
  return status.toLowerCase().includes('reject');
};

// Helper function to extract rejection reason from task notes
const getRejectionReason = (task) => {
  // First try to get from WigRejectedReason field
  if (task.wigRejectedReason || task.WigRejectedReason) {
    return task.wigRejectedReason || task.WigRejectedReason;
  }

  // Fall back to extracting from TaskNotes
  const notes = task.taskNotes || task.TaskNotes || '';
  const rejectionPrefix = 'CLIENT REJECTED: ';

  if (notes.includes(rejectionPrefix)) {
    // Extract the rejection reason
    const startIndex = notes.indexOf(rejectionPrefix) + rejectionPrefix.length;
    const remainingText = notes.substring(startIndex);

    // Find the end of the rejection reason (either | or end of string)
    const endIndex = remainingText.indexOf('|');
    const rejectionReason = endIndex !== -1
      ? remainingText.substring(0, endIndex).trim()
      : remainingText.trim();

    return rejectionReason;
  }

  return 'No reason provided';
};

// Helper function to group rejected tasks by rejection cycle (WigRejectedHistory for unique grouping)
const groupByRejectionCycle = (tasks) => {
  const groups = {};

  tasks.forEach((task, index) => {

    // Use WigRejectedHistory as the grouping key (includes timestamp + user + reason)
    // Format: "Rejected on 2025-12-22 12:19:56 by DidiP: hehe"
    // This ensures tasks with same date/time/user/reason are grouped together
    const rejectionHistory = task.wigRejectedHistory || task.WigRejectedHistory;
    const rejectionDate = task.wigRejectedDate || task.WigRejectedDate;
    const rejectionBy = task.wigRejectedBy || task.WigRejectedBy;

    // IMPORTANT: Extract reason from TaskNotes to match what's displayed
    const rejectionReason = getRejectionReason(task);

    // Create a more robust grouping key
    // If we have WigRejectedHistory, use it. Otherwise, construct from components
    let rejectionKey;
    if (rejectionHistory) {
      rejectionKey = rejectionHistory;
    } else if (rejectionDate) {
      // Construct a key from available data
      rejectionKey = `Rejected on ${formatDateTimeForUser(rejectionDate, currentUser)}${rejectionBy ? ` by ${rejectionBy}` : ''}${rejectionReason ? `: ${rejectionReason}` : ''}`;
    } else {
      rejectionKey = 'Unknown Date';
    }

    if (!groups[rejectionKey]) {
      groups[rejectionKey] = {
        tasks: [],
        // Extract the actual date for sorting
        date: rejectionDate || null,
        by: rejectionBy || 'Unknown',
        reason: rejectionReason
      };
    }

    groups[rejectionKey].tasks.push(task);
  });


  // Sort groups by actual rejection date (most recent first)
  return Object.entries(groups).sort(([keyA, groupA], [keyB, groupB]) => {
    // Always put "Unknown Date" at the end
    if (keyA === 'Unknown Date') return 1;
    if (keyB === 'Unknown Date') return -1;

    const dateA = groupA.date;
    const dateB = groupB.date;

    if (!dateA) return 1;
    if (!dateB) return -1;

    // Sort by date descending (most recent first)
    return new Date(dateB) - new Date(dateA);
  });
};

const rejectedTasks = allSortedTasks.filter(task => isRejectedTask(task));
const normalTasks = allSortedTasks.filter(task => !isRejectedTask(task));
const groupedRejectedTasks = groupByRejectionCycle(rejectedTasks);
const tasksToShow = showRejectedTasks ? allSortedTasks : normalTasks;
const rejectedTasksCount = rejectedTasks.length;

  const handleClientRejectOrder = () => {
    setShowRejectionModal(true);
    };

    const handleRejectionComplete = async (rejectionData) => {   
    if (orderData.orderDetails?.orderNo) {
        try {
        console.log('🔄 Refreshing order data after rejection...');
        await fetchOrderData(orderData.orderDetails.orderNo);
        
        // Optionally show a success message
        toast.success('Client rejection process completed successfully!');
        } catch (error) {
        console.error('❌ Error refreshing order data:', error);
        }
    }
    };
      const fetchRoomTasks = async (orderNo) => {
        try {
          setLoadingTasks(prev => new Set([...prev, orderNo]));
          
          const response = await fetch(`${API_URL}/api/room-tasks/${orderNo}`);
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          setRoomTasks(prev => ({
            ...prev,
            [orderNo]: Array.isArray(data) ? data : []
          }));
        } catch (err) {
          console.error('Error fetching room tasks:', err);
          setRoomTasks(prev => ({
            ...prev,
            [orderNo]: []
          }));
        } finally {
          setLoadingTasks(prev => {
            const newSet = new Set(prev);
            newSet.delete(orderNo);
            return newSet;
          });
        }
      };
      

    const handleAutoCompleteSuccess = (orderNo, data) => {
    console.log('🔄 Auto-complete success, refreshing production schedule...');
    // Refresh the complete order data to update all tasks and status
    fetchOrderData(orderNo);

    // Call parent callback if provided (for OrdersList to refresh the row)
    if (onAutoCompleteSuccessFromParent) {
      onAutoCompleteSuccessFromParent(orderNo, data);
    }
    };



  return (
    <div className="production-schedule-container">
      <div className="card shadow-sm">             
        <div>               
        
           
          <div className="task-table-container">
            <table className="task-table">
              <thead className="task-table-header">
                <tr>
                  <th style={{width: '6%'}}>Room </th>
                  <th style={{width: '9%'}}>Worker</th>
                  <th style={{width: '14%'}}>Task</th>
                  <th style={{width: '8%', textAlign: 'center'}}>Due Date</th>
                  <th style={{width: '6%', textAlign: 'center'}}>From</th>
                  <th style={{width: '6%', textAlign: 'center'}}>To</th>
                  <th style={{width: '10%'}}>Scan In</th>
                  <th style={{width: '10%'}}>Scan Out</th>
                  <th style={{width: '20%'}}>Task Notes</th>
                  <th style={{width: '20%'}}>QC Notes</th>
                  <th style={{width: '11%', textAlign: 'center', fontWeight: 'bold'}}>Status</th>
                  <th style={{width: '4%'}}></th>
                </tr>
              </thead>
              <tbody>
                {showRejectedTasks ? (
                  // Show rejected tasks grouped by rejection cycle
                  groupedRejectedTasks.length > 0 ? (
                    groupedRejectedTasks.map(([rejectionKey, groupData]) => (
                      <React.Fragment key={rejectionKey}>
                        {/* Rejection Cycle Header */}
                        <tr className="rejection-cycle-header">
                          <td colSpan="12" style={{
                            backgroundColor: '#f83b4bff',
                            padding: '12px',
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                            borderTop: '2px solid #dc3545',
                            borderBottom: '2px solid #dc3545'
                          }}>
                            {/* <i className="fas fa-times-circle mr-2" style={{color: '#dc3545'}}></i> */}
                            Rejection Cycle: {rejectionKey !== 'Unknown' ? rejectionKey : 'Unknown Date'}
                            <span style={{marginLeft: '10px', fontSize: '0.9rem', fontWeight: 'normal'}}>
                              ({groupData.tasks.length} task{groupData.tasks.length !== 1 ? 's' : ''})
                            </span>
                          </td>
                        </tr>
                        {/* Tasks in this rejection cycle */}
                        {groupData.tasks.map((task, index) => {
                          const isRejected = task.status === 'client_reject' || task.taskStatus === 'client_reject';
                          const taskId = task.ID || task.id;

                          return (
                            <tr
                              key={`${rejectionKey}-${index}`}
                              className={`${isRejected ? 'rejected-task' : ''} ${
                                (task.transferFromRoomNo || task.TransferFromRoomNo) &&
                                !(task.transferToRoomNo || task.TransferToRoomNo)
                                  ? 'current-location'
                                  : ''
                              }`}
                            >

                              {/* Room - Editable */}
                              <td>
                          
                                <EditableCell
                                  value={task.roomNo}
                                  onSave={(value) => updateTaskField(taskId, 'RoomNo', value)}
                                  onSaveComplete={handleRefreshSchedule}
                                  placeholder="Room No"
                                  disabled={isRejected}
                                  type="text"
                                />
                              </td>
                              <td>
                                <EditableCell
                                  value={task.assignedWorker}
                                  onSave={(value) => updateTaskField(taskId, 'AssignedWorker', value)}
                                  onSaveComplete={handleRefreshSchedule}
                                  placeholder="Unassigned"
                                  disabled={isRejected}
                                />
                              </td>

                              {/* Task - Editable */}
                              <td>
                                <EditableCell
                                  value={task.roomTask}
                                  onSave={(value) => updateTaskField(taskId, 'RoomTask', value)}
                                  onSaveComplete={handleRefreshSchedule}
                                  placeholder="No task"
                                  disabled={isRejected}
                                />
                              </td>

                              {/* Due Date - Editable */}
                              <td>
                              <EditableCell
                                value={task.taskDueDate ? utcToLocalDate(task.taskDueDate) : ''}
                                onSave={(value) => {
                                  // Convert local date to UTC before saving
                                  const utcDate = value ? localDateTimeToUTC(value + 'T00:00:00') : null;
                                  updateTaskField(taskId, 'TaskDueDate', utcDate);
                                }}
                                onSaveComplete={handleRefreshSchedule}
                                type="date"
                                disabled={isRejected}
                                />
                              </td>

                              {/* From Room - Editable */}
                              <td>
                                <EditableCell
                                  value={task.transferFromRoomNo || task.TransferFromRoomNo}
                                  onSave={(value) => updateTaskField(taskId, 'TransferFromRoomNo', value)}
                                  onSaveComplete={handleRefreshSchedule}
                                  placeholder="→"
                                  disabled={isRejected}
                                />
                              </td>

                              {/* To Room - Editable */}
                              <td>
                                <EditableCell
                                  value={task.transferToRoomNo || task.TransferToRoomNo}
                                  onSave={(value) => updateTaskField(taskId, 'TransferToRoomNo', value)}
                                  onSaveComplete={handleRefreshSchedule}
                                  placeholder="→"
                                  disabled={isRejected}
                                />
                              </td>

                              {/* Scan In - Editable - */}
                              <td>
                                <EditableCell
                                  value={
                                    task.scanInDate
                                      ? (task.scanInDate)
                                      : (task.ScanInDate ? (task.ScanInDate) : '')
                                  }
                                  onSave={async (value) => {
                                    const previousValue = task.scanInDate || task.ScanInDate;
                                    await updateTaskField(taskId, 'ScanInDate', value);
                                    // If scan in was previously null and now has a value, set status to In-Process
                                    if (!previousValue && value) {
                                      await updateTaskField(taskId, 'TaskStatus', 'In-Process');
                                    }
                                  }}
                                  onSaveComplete={handleRefreshSchedule}
                                  type="datetime-local"
                                  disabled={isRejected}
                                />
                              </td>

                              {/* Scan Out - Editable */}
                              <td>
                                <EditableCell
                                  value={
                                    task.scanOutDate
                                      ? (task.scanOutDate)
                                      : (task.ScanOutDate ? (task.ScanOutDate) : '')
                                  }
                                  onSave={async (value) => {
                                    await updateTaskField(taskId, 'ScanOutDate', value);
                                    // If scan out has a value (whether previously null or not), set status to Task Completed
                                    if (value) {
                                      await updateTaskField(taskId, 'TaskStatus', 'Task Completed');
                                    }
                                  }}
                                  onSaveComplete={handleRefreshSchedule}
                                  type="datetime-local"
                                  disabled={isRejected}
                                />
                              </td>

                              {/* Task Notes - Editable */}
                              <td>
                                <EditableCell
                                  value={task.taskNotes}
                                  onSave={(value) => updateTaskField(taskId, 'TaskNotes', value)}
                                  onSaveComplete={handleRefreshSchedule}
                                  placeholder="Add notes..."
                                  multiline={true}
                                  disabled={isRejected}
                                />
                              </td>

                              {/* QC Notes - Editable */}
                              <td>
                                <EditableCell
                                  value={task.qcNotes || task.QC_Notes}
                                  onSave={(value) => updateTaskField(taskId, 'QC_Notes', value)}
                                  onSaveComplete={handleRefreshSchedule}
                                  placeholder="Add QC notes..."
                                  multiline={true}
                                  disabled={isRejected}
                                />
                              </td>

                              {/* Status - Editable Select */}
                              <td>
                                <EditableCell
                                  value={task.status || task.taskStatus}
                                  onSave={(value) => updateTaskField(taskId, 'TaskStatus', value)}
                                  onSaveComplete={handleRefreshSchedule}
                                  type="select"
                                  options={statusOptions}
                                  placeholder="Select status..."
                                  className={`status-badge-fresh status-${(task.status || task.taskStatus)?.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                                  disabled={isRejected}
                                />
                              </td>

                              {/* Actions - Just Delete */}
                              <td>
                                <button
                                  className="delete-btn"
                                  onClick={() => {
                                      deleteTask(taskId);
                                  }}
                                  title="Delete Task"
                                  disabled={isRejected}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="12" className="empty-state">
                        <i className="fas fa-info-circle"></i>
                        No rejected tasks found
                      </td>
                    </tr>
                  )
                ) : (
                  // Show normal tasks (non-rejected)
                  tasksToShow.map((task, index) => {
                  const isRejected = task.status === 'client_reject' || task.taskStatus === 'client_reject';
                  const taskId = task.ID || task.id;
                  
                  return (
                    <tr 
                      key={index}
                      className={`${isRejected ? 'rejected-task' : ''} ${
                        (task.transferFromRoomNo || task.TransferFromRoomNo) &&
                        !(task.transferToRoomNo || task.TransferToRoomNo)
                          ? 'current-location'
                          : ''
                      }`}
                    >                        
                      
                      {/* Room - Editable */}
                      <td>
                        {isRejected && (
                          <div
                            style={{
                              backgroundColor: '#dc2626',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              marginBottom: '4px',
                              display: 'inline-block',
                              maxWidth: '100%',
                              wordWrap: 'break-word'
                            }}
                            title={`Rejection Reason: ${getRejectionReason(task)}`}
                          >
                            REJECTED: {getRejectionReason(task)}
                          </div>
                        )}
                        <EditableCell
                          value={task.roomNo}
                          onSave={(value) => updateTaskField(taskId, 'RoomNo', value)}
                          onSaveComplete={handleRefreshSchedule}
                          placeholder="Room No"
                          disabled={isRejected}
                          type="text"
                        />
                      </td>
                      <td>
                        <EditableCell
                          value={task.assignedWorker}
                          onSave={(value) => updateTaskField(taskId, 'AssignedWorker', value)}
                          onSaveComplete={handleRefreshSchedule}  
                          placeholder="Unassigned"
                          disabled={isRejected}
                        />
                      </td>
                      
                      {/* Task - Editable */}
                      <td>
                        <EditableCell
                          value={task.roomTask}
                          onSave={(value) => updateTaskField(taskId, 'RoomTask', value)}
                          onSaveComplete={handleRefreshSchedule}  
                          placeholder="No task"
                          disabled={isRejected}
                        />
                      </td>
                      
                      {/* Due Date - Editable */}
                      <td>
                      <EditableCell
                        value={task.taskDueDate ? utcToLocalDate(task.taskDueDate) : ''}
                        onSave={(value) => {
                          // Convert local date to UTC before saving
                          const utcDate = value ? localDateTimeToUTC(value + 'T00:00:00') : null;
                          updateTaskField(taskId, 'TaskDueDate', utcDate);
                        }}
                        onSaveComplete={handleRefreshSchedule}  
                        type="date"
                        disabled={isRejected}
                        />
                      </td>
                      
                      {/* From Room - Editable */}
                      <td>
                        <EditableCell
                          value={task.transferFromRoomNo || task.TransferFromRoomNo}
                          onSave={(value) => updateTaskField(taskId, 'TransferFromRoomNo', value)}
                          onSaveComplete={handleRefreshSchedule}  
                          placeholder="→"
                          disabled={isRejected}
                        />
                      </td>
                      
                      {/* To Room - Editable */}
                      <td>
                        <EditableCell
                          value={task.transferToRoomNo || task.TransferToRoomNo}
                          onSave={(value) => updateTaskField(taskId, 'TransferToRoomNo', value)}
                          onSaveComplete={handleRefreshSchedule}  
                          placeholder="→"
                          disabled={isRejected}
                        />
                      </td>
                      
                      {/* Scan In - Editable - */}
                      <td>
                        <EditableCell
                          value={
                            task.scanInDate
                              ? (task.scanInDate)
                              : (task.ScanInDate ? (task.ScanInDate) : '')
                          }
                          onSave={async (value) => {
                            const previousValue = task.scanInDate || task.ScanInDate;
                            await updateTaskField(taskId, 'ScanInDate', value);
                            // If scan in was previously null and now has a value, set status to In-Process
                            if (!previousValue && value) {
                              await updateTaskField(taskId, 'TaskStatus', 'In-Process');
                            }
                          }}
                          onSaveComplete={handleRefreshSchedule}
                          type="datetime-local"
                          disabled={isRejected}
                        />
                      </td>

                      {/* Scan Out - Editable */}
                      <td>
                        <EditableCell
                          value={
                            task.scanOutDate
                              ? (task.scanOutDate)
                              : (task.ScanOutDate ? (task.ScanOutDate) : '')
                          }
                          onSave={async (value) => {
                            await updateTaskField(taskId, 'ScanOutDate', value);
                            // If scan out has a value (whether previously null or not), set status to Task Completed
                            if (value) {
                              await updateTaskField(taskId, 'TaskStatus', 'Task Completed');
                            }
                          }}
                          onSaveComplete={handleRefreshSchedule}
                          type="datetime-local"
                          disabled={isRejected}
                        />
                      </td>
                      
                      {/* Task Notes - Editable */}
                      <td>
                        <EditableCell
                          value={task.taskNotes}
                          onSave={(value) => updateTaskField(taskId, 'TaskNotes', value)}
                          onSaveComplete={handleRefreshSchedule}  
                          placeholder="Add notes..."
                          multiline={true}
                          disabled={isRejected}
                        />
                      </td>

                      {/* QC Notes - Editable */}
                      <td>
                        <EditableCell
                          value={task.qcNotes || task.QC_Notes}
                          onSave={(value) => updateTaskField(taskId, 'QC_Notes', value)}
                          onSaveComplete={handleRefreshSchedule}  
                          placeholder="Add QC notes..."
                          multiline={true}
                          disabled={isRejected}
                        />
                      </td>
                      
                      {/* Status - Editable Select */}
                      <td>                           
                        <EditableCell
                          value={task.status || task.taskStatus}
                          onSave={(value) => updateTaskField(taskId, 'TaskStatus', value)}
                          onSaveComplete={handleRefreshSchedule}  
                          type="select"
                          options={statusOptions}
                          placeholder="Select status..."
                          className={`status-badge-fresh status-${(task.status || task.taskStatus)?.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                          disabled={isRejected}
                        />
                      </td>
                      
                      {/* Actions - Just Delete */}
                      <td>
                        <button 
                          className="delete-btn"
                          onClick={() => {
                              deleteTask(taskId);              
                          }}
                          title="Delete Task"
                          disabled={isRejected}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })
                )}
                {!showRejectedTasks && tasksToShow.length === 0 && (
                  <tr>
                    <td colSpan="12" className="empty-state">
                      <i className="fas fa-info-circle"></i>
                      No active tasks found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

        </div>  
        
        {/* Add Task Button Row */}
        <div className="actions-row" >
          <button 
                className="btn-transfer-toggle"
                onClick={() => {
                    if (orderData.orderDetails?.orderNo) {
                        openTransferModal(orderData.orderDetails.orderNo);
                    } else {
                        alert('No order number available for transfer');
                    }
                }}
                disabled={!orderData.orderDetails?.orderNo}
                title={orderData.orderDetails?.orderNo ? "Open Transfer Modal" : "No order selected"}
              >
                <i className="fas fa-exchange-alt"></i>
                <span className="btn-transfer-label"> Transfer Wig</span>
              </button>
          
          {/* Client Reject Order Button - Left side */}
          <button 
            className="client-reject-btn"
            onClick={handleClientRejectOrder}
            title="Reject Order and Start Corrective Work"
          >
            <i className="fas fa-times-circle mr-2"></i>
            Client Reject Order
          </button>

          <button
            className={`show-rejected-toggle ${showRejectedTasks ? 'active' : ''}`}
            onClick={() => setShowRejectedTasks(!showRejectedTasks)}
            title={showRejectedTasks ? "Hide rejected tasks" : "Show rejected tasks"}
          >
            <i className="fas fa-times-circle"></i>
            <span className="show-rejected-label">{showRejectedTasks ? 'Hide' : 'Show'} Rejected ({rejectedTasksCount})</span>
          </button>

          {/* Add Task Button - Right side */}
            <button
              className="btn btn-success rounded-pill shadow-sm media-btn"
              style={{backgroundColor : '#0bdbe2'}}
              onClick={() => setShowMediaModal(true)}
              title="View or attach media for this order"
            >
              <i className="fas fa-video"></i><span className="media-btn-label"> Media</span>
            </button>
            <AutoCompleteButton
                orderNo={orderNo}
                className="auto-complete-btn-primary"
                variant="success"
                size="sm"
                onAutoCompleteSuccess={handleAutoCompleteSuccess}
                />

                     <div className="reminder-btn-wrapper">
            <button
              className="btn btn-warning rounded-circle add-task-btn-inline"
              onClick={() => setShowReminderModal(true)}
              title={activeOrderReminders.length > 0 ? `${activeOrderReminders.length} active reminder${activeOrderReminders.length > 1 ? 's' : ''} for this order` : 'Set Reminder for this Order'}
            >
              <i className="fas fa-clock"></i>
            </button>
            {activeOrderReminders.length > 0 && (
              <span className="reminder-active-badge">
                {activeOrderReminders.length}
              </span>
            )}
          </div>
          <button 
            className="btn btn-primary rounded-circle add-task-btn-inline" 
            onClick={() => {
              setEditingTask(null);
              setNewTask({
                location: '',
                roomNo: '',
                roomTask: '',
                assignedWorker: '',
                taskDueDate: '',
                taskNotes: '',
                qcNotes: '',
                status: 'Not Started'
              });
              setShowAddTaskModal(true);
            }}
            title="Add New Task"
          >
            <i className="fas fa-plus"></i>
          </button>

     


        </div>
      </div>
      
      {/* Add/Edit Task Modal */}
      {showAddTaskModal && (
        <div className="task-modal-overlay">
          <div className="task-modal-content">
            <div className="task-modal-header">
              <h5>{editingTask ? 'Edit Task' : 'Add New Task'}</h5>
              <button 
                className="close-modal-btn"
                onClick={() => {
                  setShowAddTaskModal(false);
                  setEditingTask(null);
                  setNewTask({
                    location: '',
                    roomNo: '',
                    roomTask: '',
                    assignedWorker: '',
                    taskDueDate: '',
                    taskNotes: '',
                    qcNotes: '',
                    status: 'Not Started'
                  });
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="task-modal-body">
              {/* Form fields */}
              <div className="form-group">
                <label>Location: <span className="text-danger">*</span></label>
                <select 
                  className="form-control"
                  value={newTask.location}
                  onChange={(e) => setNewTask({...newTask, location: e.target.value})}
                >
                  <option value="">Select Location</option>
                  <option value="Pomona">Pomona</option>
                  <option value="Lakewood">Lakewood</option>
                  <option value="Israel">Israel</option>
                  <option value="China">China</option>
                </select>
              </div>

              <div className="form-group">
                <label>Room: <span className="text-danger">*</span></label>
                <select 
                  className="form-control"
                  value={newTask.roomNo}
                  onChange={(e) => handleRoomChange(newTask.location, e.target.value)}
                  disabled={!newTask.location}
                >
                  <option value="">Select Room</option>
                  {(roomsData[newTask.location]?.rooms || []).map(room => (
                    <option key={room.roomNo} value={room.roomNo}>
                      {room.roomName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Enhanced Task field */}
              <div className="form-group">
                <label>Task: <span className="text-danger">*</span></label>
                <select 
                  className="form-control"
                  value={newTask.roomTask}
                  onChange={(e) => setNewTask({...newTask, roomTask: e.target.value})}
                  disabled={!newTask.roomNo}
                >
                  <option value="">Select Task</option>
                  {(() => {
                    const location = newTask.location;
                    const roomNo = newTask.roomNo;
                    const tasks = roomsData[location]?.tasks?.[roomNo] || [];
                    
                    if (tasks.length === 0) {
                      return <option value="" disabled>No tasks available for this room</option>;
                    }
                    
                    return tasks.map((task, index) => {
                      const taskDescription = 
                        task.TaskDescription || 
                        task.taskDescription || 
                        task.Description || 
                        task.description ||
                        task.RoomTask ||
                        task.roomTask ||
                        task.Task ||
                        task.task ||
                        task.Name ||
                        task.name ||
                        task.Title ||
                        task.title ||
                        Object.values(task).find(value => typeof value === 'string' && value.trim().length > 0) ||
                        `Task ${index + 1}`;
                      
                      return (
                        <option key={index} value={taskDescription}>
                          {taskDescription}
                        </option>
                      );
                    });
                  })()}
                </select>
              </div>

              <div className="form-group">
                <label>Assign To:</label>
                <select 
                  className="form-control"
                  value={newTask.assignedWorker}
                  onChange={(e) => setNewTask({...newTask, assignedWorker: e.target.value})}
                  disabled={!newTask.roomNo}
                >
                  <option value="">Select Worker</option>
                  {(roomsData[newTask.location]?.workers?.[newTask.roomNo] || []).map((worker, index) => (
                    <option key={index} value={worker.userName || worker.UserName}>
                      {worker.UserName || worker.userName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select 
                  className="form-control"
                  value={newTask.status}
                  onChange={(e) => setNewTask({...newTask, status: e.target.value})}
                >
                  <option value="Pending">Pending</option>
                  <option value="In Progress">In Process</option>
                  <option value="Completed">Task Completed</option>
                  <option value="Not Started">Not Started</option>
                </select>
              </div>

              <div className="form-group">
                <label>Due Date:</label>
                <input 
                  type="date" 
                  className="form-control date-input-custom"
                  value={newTask.taskDueDate}
                  onChange={(e) => setNewTask({...newTask, taskDueDate: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Notes:</label>
                <textarea 
                  className="form-control"
                  value={newTask.taskNotes}
                  onChange={(e) => setNewTask({...newTask, taskNotes: e.target.value})}
                  rows="3"
                  style={{ 
                    fontSize: '1.5rem', 
                    lineHeight: '1.4',
                    fontFamily: 'inherit'
                  }}
                ></textarea>
              </div>
                
              <div className="form-group">
                <label>QC Notes:</label>
                <textarea 
                  className="form-control"
                  value={newTask.qcNotes || ''}
                  onChange={(e) => setNewTask({...newTask, qcNotes: e.target.value})}
                  rows="3"
                  style={{ 
                    fontSize: '1.5rem', 
                    lineHeight: '1.4',
                    fontFamily: 'inherit'
                  }}
                ></textarea>
              </div>
            </div>
            
            <div className="task-modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddTaskModal(false);
                  setEditingTask(null);
                  setNewTask({
                    location: '',
                    roomNo: '',
                    roomTask: '',
                    assignedWorker: '',
                    taskDueDate: '',
                    taskNotes: '',
                    qcNotes: '',
                    status: 'Not Started'
                  });
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={editingTask ? updateTask : addTaskToOrder}
                disabled={!newTask.roomNo || !newTask.roomTask}
              >
                {editingTask ? 'Update Task' : 'Add Task'}
              </button>
            </div>
          </div>
        </div>
      )}

    <WigTransferModal />

    <AttachMediaModal
      isOpen={showMediaModal}
      onClose={() => setShowMediaModal(false)}
      orderNo={orderNo}
      roomTasks={orderData.roomTasks}
    />
  
      {showRejectionModal && (
        <ClientRejectionModal
            isOpen={showRejectionModal}
            onClose={() => setShowRejectionModal(false)}
            orderData={orderData}
            onRejectionComplete={handleRejectionComplete}
            currentUser={currentUser}
        />
    )}

    {showReminderModal && (
      <ReminderFormModal
        username={currentUser}
        defaultOrderNo={orderData.orderDetails?.orderNo}
        defaultTitle={orderData.orderDetails?.customerName || ''}
        onClose={() => setShowReminderModal(false)}
        onSaved={() => { toast.success('Reminder set!'); fetchOrderReminders(); }}
      />
    )}
    </div>
  );
};

export default ProductionSchedule;